import { cn } from '@mdxui/primitives/lib/utils'
import { Badge, Button } from '@mdxui/primitives'
import { formatDate as defaultFormatDate } from '../lib/utils'
import type { ComponentType, ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'

/**
 * Blog post author
 */
export interface BlogPostAuthor {
  name: string
  image?: string
}

/**
 * Blog post data
 */
export interface BlogPost {
  /** Post slug (used for URL and gradient seed) */
  slug: string
  /** Post title */
  title: string
  /** Post description/excerpt */
  description?: string
  /** Publication date (ISO string or formatted) */
  date?: string
  /** Post author */
  author?: BlogPostAuthor
  /** Post tags/categories */
  tags?: string[]
  /** Optional custom image URL (overrides gradient) */
  image?: string
}

export interface BlogCardLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface BlogCardProps {
  /** Blog post data */
  post: BlogPost
  /** Custom link component for framework routing */
  LinkComponent?: ComponentType<BlogCardLinkProps>
  /** Format date function */
  formatDate?: (date: string) => string
  /** Show author info (default: true) */
  showAuthor?: boolean
  /** Base path for post links (default: '') — e.g. '/blog' produces '/blog/{slug}' */
  basePath?: string
  /** Additional className */
  className?: string
}

/**
 * Default link component (anchor tag)
 */
function DefaultLink({ href, children, className }: BlogCardLinkProps) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  )
}

export function BlogCard({
  post,
  LinkComponent = DefaultLink,
  formatDate = defaultFormatDate,
  showAuthor = false,
  basePath = '',
  className,
}: BlogCardProps) {
  const href = `${basePath}/${post.slug}`

  return (
    <LinkComponent href={href} className={cn('group block relative', className)}>
      <article className='flex flex-col h-full p-4 bg-card border border-border rounded-[var(--radius-lg,var(--radius))] transition-colors group-hover:border-foreground/40'>
        {/* Date */}
        {post.date && <time className='block text-xs font-mono font-medium text-muted-foreground mb-2'>{formatDate(post.date)}</time>}

        {/* Title + Description */}
        <div className='my-3 min-h-[6.5rem]'>
          <h3 className='text-xl font-semibold text-card-foreground'>{post.title}</h3>
          {post.description && <p className='text-card-foreground/80 text-base line-clamp-3 mt-1'>{post.description}</p>}
        </div>

        {/* Author */}
        {showAuthor && post.author && (
          <div className='flex items-center gap-2 text-sm text-muted-foreground mt-3'>
            {post.author.image && <img src={post.author.image} alt={post.author.name} className='w-6 h-6 rounded-full object-cover' />}
            <span>By {post.author.name}</span>
          </div>
        )}

        {/* Footer: Tags + Read button */}
        <div className='flex justify-between items-center mt-auto pt-4'>
          {/* Tags */}
          {post.tags && post.tags.length > 0 ? (
            <div className='flex flex-wrap gap-2'>
              {post.tags.map((tag) => (
                <Badge key={tag} variant='secondary'>
                  {tag}
                </Badge>
              ))}
            </div>
          ) : (
            <div />
          )}

          {/* Read button */}
          <Button variant='link' className='text-muted-foreground group-hover:text-foreground hover:no-underline transition-colors'>
            Read <ChevronRight className='w-4 h-4' />
          </Button>
        </div>
      </article>
    </LinkComponent>
  )
}
